/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import ErrorIcon from '@mui/icons-material/esm/Error';
import * as Colors from '@mui/material/colors/index';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { rename } from './AppGraphQL';

function AppName(props) {
	const [name, setName] = useState('');
	const [nameError, setNameError] = useState();

	useEffect(() => {
		setName(props.name);
	}, [props.name]);

	const isNameValid = (newName) => newName.length > 0;

	const validateName = (newName) => {
		if (newName === props.name) {
			setNameError(undefined);
			return true;
		}

		if (!newName || newName.length < 1) {
			setNameError(
				props.intl.formatMessage({
					id: 'Error.EmptyName',
					defaultMessage: ''
				})
			);
			return false;
		}
		if (!isNameValid(newName)) {
			setNameError(
				props.intl.formatMessage({
					id: 'Error.InvalidName',
					defaultMessage: ''
				})
			);
			return false;
		}

		setNameError(undefined);

		return true;
	};

	const handleKeyPress = (event) => {
		if (event.key === 'Enter') {
			if (document.activeElement) {
				document.activeElement.blur();
			}
			event.preventDefault();
		}
	};

	const handleSaveName = async (event) => {
		if (validateName(event.target.value)) {
			const result = await rename(props.appId, event.target.value);
			if (result === 'CONFLICT') {
				setNameError(
					props.intl.formatMessage({
						id: 'Error.DuplicateName',
						defaultMessage: ''
					})
				);
			}
		}
	};

	const handleChangeName = (event) => {
		setName(event.target.value);
	};

	const nameSaved = true;
	const fakeEle = document.createElement('div');
	let calcWidth = '200px';

	const textboxEle = document.getElementById('app-name');
	if (textboxEle) {
		// Hide it completely
		fakeEle.style.position = 'absolute';
		fakeEle.style.top = '0';
		fakeEle.style.left = '-9999px';
		fakeEle.style.overflow = 'hidden';
		fakeEle.style.visibility = 'hidden';
		fakeEle.style.whiteSpace = 'nowrap';
		fakeEle.style.height = '0';

		// Get the styles
		const styles = window.getComputedStyle(textboxEle);

		// Copy font styles from the textbox
		fakeEle.style.fontFamily = styles.fontFamily;
		fakeEle.style.fontSize = styles.fontSize;
		fakeEle.style.fontStyle = styles.fontStyle;
		fakeEle.style.fontWeight = styles.fontWeight;
		fakeEle.style.letterSpacing = styles.letterSpacing;
		fakeEle.style.textTransform = styles.textTransform;

		fakeEle.style.borderLeftWidth = styles.borderLeftWidth;
		fakeEle.style.borderRightWidth = styles.borderRightWidth;
		fakeEle.style.paddingLeft = styles.paddingLeft;
		fakeEle.style.paddingRight = styles.paddingRight;

		// Append the fake element to `body`
		document.body.appendChild(fakeEle);

		fakeEle.innerHTML = name.replace(/\s/g, '&nbsp;');

		const fakeEleStyles = window.getComputedStyle(fakeEle);
		calcWidth = fakeEleStyles.width;
		document.body.removeChild(fakeEle);
	}

	return (
		<div
			style={
				{
					// minWidth: '100px'
				}
			}
		>
			<Tooltip
				enterDelay={300}
				title={<FormattedMessage id="Tooltip.EditName" defaultMessage="Edit Machine Name" />}
			>
				<TextField
					error={typeof nameError === 'string'}
					id="app-name"
					spellCheck={false}
					variant="standard"
					// disabled={!!this.props.disabled}
					value={name || ''}
					onBlur={handleSaveName}
					onChange={handleChangeName}
					onKeyPress={handleKeyPress}
					inputProps={{ maxLength: 200 }}
					sx={{
						// width: `${Math.max(100, name.length * 11)}px`,
						// maxWidth: `${Math.max(100, name.length * 11)}px`,
						width: calcWidth,
						maxWidth: calcWidth,
						'& .MuiInputBase-input': {
							fontSize: '1.2rem',
							color: (theme) => `${theme.components.MuiAppBar.styleOverrides.colorIcon.backgroundColor})`
						},
						'& .MuiInput-root': {
							'&:hover': {
								borderWidth: '1px',
								borderColor: (theme) =>
									`${theme.components.MuiAppBar.styleOverrides.colorIcon.backgroundColor})`
							},
							'&:hover:not(.Mui-disabled):before': {
								borderColor: (theme) =>
									`${theme.components.MuiAppBar.styleOverrides.colorIcon.backgroundColor})`
							}
						},
						'& .MuiInput-underline:before': {
							// borderColor: (theme) => `${theme.components.MuiAppBar.styleOverrides.colorIcon.backgroundColor})`,
							// borderWidth: '1px',
							border: 'none'
						},
						'& .MuiInput-underline:after': {
							borderWidth: '1px',
							borderColor: (theme) =>
								`${theme.components.MuiAppBar.styleOverrides.colorIcon.backgroundColor})`
						},
						'& .Mui-focused': {
							backgroundColor: (theme) =>
								`${theme.components.MuiAppBar.styleOverrides.colorIcon.backgroundColor}, 0.1)`,
							borderColor: (theme) =>
								`${theme.components.MuiAppBar.styleOverrides.colorIcon.backgroundColor})`
						}
					}}
					// eslint-disable-next-line react/jsx-no-duplicate-props
					InputProps={
						!nameSaved
							? {
									endAdornment: (
										<InputAdornment position="end">
											<ErrorIcon color={Colors.red[100]} />
										</InputAdornment>
									)
							  }
							: {}
					}
				/>
			</Tooltip>
			{typeof nameError === 'string' ? (
				<Typography
					sx={{
						display: 'inline',
						lineHeight: '36px',
						padding: '14px',
						color: 'red'
					}}
				>
					{nameError}
				</Typography>
			) : null}
		</div>
	);
}

export default injectIntl(AppName);
